import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ClaimsResponse, ClaimResponse } from '../login/objects/claimsResponse';
import { environment } from '../environments/environment';
import { MsalService } from '@azure/msal-angular';

export interface CreateUserRequest {
	email: string;
	firstName: string;
	lastName: string;
	partnerId?: number;
	roleID?: string;
	manufacturerId?: string;
}

export interface UpdateUserRequest extends Partial<Omit<UserData, 'id'>> {
	partnerId?: number | null;
	emailConfirmed?: boolean;
	userName?: string;
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	roleID?: string;
	phoneNumberConfirmed?: boolean;
	twoFactorEnabled?: boolean;
	lockoutEnabled?: boolean;
	disabledDate?: string | null;
}

export interface UserResponse {
	userId: string;
	email: string;
	userName: string;
	phoneNumber: string;
	partnerId?: number | null;
	firstName: string;
	lastName: string;
	roleID?: string;
	emailConfirmed: boolean;
	phoneNumberConfirmed: boolean;
	twoFactorEnabled: boolean;
	lockoutEndDateUtc?: string | null;
	lockoutEnabled: boolean;
	accessFailedCount: number;
	disabledDate?: string | null;
	insertDate?: string | null;
	partnerName: string;
	manufacturerId: string;
	userType: number;
}

export interface UserData extends Omit<UserResponse, 'userId'> {
	id: string;
}

export interface PermissionReply {
	permission: string;
	permissionId: number;
}

export interface GetPermissionsResponse {
	permissions: PermissionReply[];
}

export interface CombinedUserData {
	user: UserResponse;
	permissions: string[];
	hasPermission: (permission: string) => boolean;
}

@Injectable({
	providedIn: 'root'
})
export class UserService {
	private userInfoSubject = new BehaviorSubject<ClaimsResponse | null>(null);
	private combinedUserDataSubject = new BehaviorSubject<CombinedUserData | null>(null);
	private readonly apiUrl = environment.api;

	public userInfo$ = this.userInfoSubject.asObservable();
	public combinedUserData$ = this.combinedUserDataSubject.asObservable();

	constructor(
		private http: HttpClient,
		private msalService: MsalService
	) { }

	public get userInfo(): ClaimsResponse | null {
		return this.userInfoSubject.value;
	}

	public setUserInfo(userInfo: ClaimsResponse | null): void {
		this.userInfoSubject.next(userInfo);
	}

	public getClaim(claimName: string): string | null {
		const claim = this.userInfo?.claims?.find(c =>
			c.name === claimName
		);
		return claim?.value || null;
	}

	public get isLoggedIn(): boolean {
		return this.userInfo !== null && this.userInfo.claims.length > 0;
	}

	public get userName(): string | null {
		return this.getClaim('name') || this.getClaim('email');
	}

	private async getAuthHeaders(): Promise<HttpHeaders> {

		const activeAccount = this.msalService.instance.getActiveAccount();

		if (activeAccount) {
			const tokenResponse = await this.msalService.instance.acquireTokenSilent({
				scopes: [environment.azureClient],
				account: activeAccount
			});

			const headers = new HttpHeaders({
				'Content-Type': 'application/json; charset=utf-8',
				'Authorization': `Bearer ${tokenResponse.accessToken}`
			});

			return headers;
		}

		return new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' });
	}

	createUser(request: CreateUserRequest): Observable<any> {
		return new Observable<any>(observer => {
			this.getAuthHeaders().then(headers => {
				this.http.post(`${this.apiUrl}Users`, request, { headers })
					.subscribe({
						next: (response) => observer.next(response),
						error: (error) => observer.error(error),
						complete: () => observer.complete()
					});
			}).catch(error => observer.error(error));
		});
	}

	getUser(): Observable<UserResponse> {
		return new Observable<UserResponse>(observer => {
			this.getAuthHeaders().then(headers => {
				this.http.get<UserResponse>(`${this.apiUrl}Users/`, { headers })
					.subscribe({
						next: (response) => observer.next(response),
						error: (error) => observer.error(error),
						complete: () => observer.complete()
					});
			}).catch(error => observer.error(error));
		});
	}

	getPermissions(roleId: string): Observable<string[]> {
		return new Observable<string[]>(observer => {
			this.getAuthHeaders().then(headers => {
				this.http.get<GetPermissionsResponse>(`${this.apiUrl}permissions/roles/${roleId}`, { headers })
					.subscribe({
						next: (response) => {
							const permissions = response.permissions.map(p => p.permission);
							observer.next(permissions);
							observer.complete();
						},
						error: (error) => observer.error(error)
					});
			}).catch(error => observer.error(error));
		});
	}

	getCombinedUserData(): Observable<CombinedUserData> {
		return this.getUser().pipe(
			switchMap(user => this.getPermissions(user.roleID || '').pipe(
				map(permissions => ({
					user,
					permissions,
					hasPermission: (permission: string) => permissions.includes(permission)
				}))
			))
		);
	}

	loadCombinedUserData(): void {
		this.getCombinedUserData().subscribe({
			next: (combinedData) => {
				this.combinedUserDataSubject.next(combinedData);
			},
			error: (error) => {
				this.combinedUserDataSubject.next(null);
			}
		});
	}

	get currentCombinedUserData(): CombinedUserData | null {
		return this.combinedUserDataSubject.value;
	}

	getManagedUser(userId: string): Observable<UserResponse> {
		return new Observable<UserResponse>(observer => {
			this.getAuthHeaders().then(headers => {
				this.http.get<UserResponse>(`${this.apiUrl}Users/${userId}`, { headers })
					.subscribe({
						next: (response) => observer.next(response),
						error: (error) => observer.error(error),
						complete: () => observer.complete()
					});
			}).catch(error => observer.error(error));
		});
	}

	getAllUsers(partnerId: number): Observable<UserResponse[]> {
		return new Observable<UserResponse[]>(observer => {
			this.getAuthHeaders().then(headers => {
				this.http.get<{ users: UserResponse[] }>(`${this.apiUrl}Users/partner/${partnerId}`, { headers })
					.subscribe({
						next: (response) => {
							observer.next(response.users);
							observer.complete();
						},
						error: (error) => {
							observer.error(error);
						}
					});
			}).catch(error => {
				observer.error(error);
			});
		});
	}

	getUsersByManufacturer(manufacturerId: string): Observable<UserResponse[]> {
		return new Observable<UserResponse[]>(observer => {
			this.getAuthHeaders().then(headers => {
				this.http.get<{ users: UserResponse[] }>(`${this.apiUrl}Users/manufacturer/${manufacturerId}`, { headers })
					.subscribe({
						next: (response) => {
							observer.next(response.users);
							observer.complete();
						},
						error: (error) => {
							observer.error(error);
						}
					});
			}).catch(error => {
				observer.error(error);
			});
		});
	}

	getUsers(email?: string): Observable<UserResponse[]> {
		return new Observable<UserResponse[]>(observer => {
			this.getAuthHeaders().then(headers => {
				const url = email
					? `${this.apiUrl}Users/search?email=${email}`
					: `${this.apiUrl}Users/search`;
				this.http.get<UserResponse[]>(url, { headers })
					.subscribe({
						next: (response) => {
							observer.next(response);
							observer.complete()
						},
						error: (error) => observer.error(error),
						complete: () => observer.complete()
					});
			}).catch(error => observer.error(error));
		})
	}

	updateUser(userId: string, request: UpdateUserRequest): Observable<any> {
		return new Observable<any>(observer => {
			this.getAuthHeaders().then(headers => {
				this.http.put(`${this.apiUrl}Users/${userId}`, request, { headers })
					.subscribe({
						next: (response) => observer.next(response),
						error: (error) => observer.error(error),
						complete: () => observer.complete()
					});
			}).catch(error => observer.error(error));
		});
	}

	deleteUser(userId: string): Observable<any> {
		return new Observable<any>(observer => {
			this.getAuthHeaders().then(headers => {
				this.http.delete(`${this.apiUrl}Users/${userId}`, { headers })
					.subscribe({
						next: (response) => observer.next(response),
						error: (error) => observer.error(error),
						complete: () => observer.complete()
					});
			}).catch(error => observer.error(error));
		});
	}
}
